import { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Camera, Check, ImageOff, Plus, Trash2 } from 'lucide-react';
import { getAllCategories, getProduct, getSubcategories } from '../../data/catalog';
import {
  addVariantToFabric,
  deleteProduct,
  patchCustomVariant,
  removeVariant,
  slugify,
  updateCustomProduct,
} from '../../utils/customProducts';
import { PHOTO_STATUS, getPhotoStatus, patchVariantPhoto } from '../../utils/photoStatus';
import { VariantAIAnalysis } from '../../components/admin/VariantAIAnalysis';
import { fileToDataUrl } from '../../utils/siteConfig';
import { useQueue } from '../../context/QueueContext';
import PhotoReviewModal from './PhotoReviewModal';
import { CoverAIGenerator } from '../../components/admin/CoverAIGenerator';

const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-navy';
const inputCls =
  'mt-1 h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';

const ELASTICIDADE_OPTS = ['Não informado', 'Não', 'Baixa', 'Média', 'Alta'];
const UNIDADE_OPTS = ['Metro', 'Peça', 'Retalho'];
const PATTERN_OPTS = ['Liso', 'Estampado', 'Xadrez', 'Listrado', 'Jacquard'];

// Edição de produto existente: nunca cria produto novo.
// Base fixa → productOverrides/variantOverrides; cadastrados pelo painel → patch direto.
export default function AdminEditProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProduct(slug);

  const [form, setForm] = useState(() => ({
    name: product?.name || '',
    categorySlug: product?.categorySlug || '',
    subcategorySlug: product?.subcategorySlug || '',
    description: product?.description || '',
    width: product?.width || '',
    composition: product?.composition || '',
    gramatura: product?.gramatura || '',
    elasticidade: product?.elasticidade || 'Não informado',
    unidadeVenda: product?.unidadeVenda || 'Metro',
    fabricante: product?.fabricante || '',
    observacoesTecnicas: product?.observacoesTecnicas || '',
  }));
  const [variants, setVariants] = useState(() =>
    (product?.variants || []).map((v) => ({
      id: v.id,
      name: v.name,
      code: v.code || '',
      pattern: v.pattern || 'Liso',
      description: v.description || '',
      image: v.image || null,
      photoStatus: getPhotoStatus(v),
    }))
  );
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [showVariantAI, setShowVariantAI] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuccess, setAiSuccess] = useState('');
  const { user } = useQueue();
  const fileRef = useRef(null);
  const photoTargetRef = useRef(null);
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [confirmRemovePhoto, setConfirmRemovePhoto] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cover, setCover] = useState(product?.coverImage || null);
  const [saving, setSaving] = useState(false);
  // Variantes marcadas para remoção: só são excluídas de fato ao SALVAR (pode desfazer antes).
  const [removedIds, setRemovedIds] = useState([]);
  // Preserva o filtro de categoria da tela Produtos ao voltar (?cat=...).
  const [searchParams] = useSearchParams();
  const backTo = `/admin/produtos${searchParams.get('cat') ? `?cat=${encodeURIComponent(searchParams.get('cat'))}` : ''}`;
  const variantless = product?.hasVariants === false;
  const activeVariants = variants.filter((v) => !removedIds.includes(v.id));

  const addVariantRow = () =>
    setVariants((vs) => [
      ...vs,
      { id: `nova-${Date.now()}`, name: '', code: '', pattern: 'Liso', description: '', image: null, photoStatus: null, isNew: true },
    ]);

  // Capa comercial gerada por IA: gravada no servidor (MongoDB) via API.
  const saveCover = (value) => {
    updateCustomProduct(slug, { coverImage: value }).catch((e) => setError(e.message || 'Falha ao salvar a capa.'));
    setCover(value);
  };

  // Exclusão: remove o produto e suas variantes do catálogo (confirmação obrigatória).
  const doDelete = async () => {
    try {
      await deleteProduct(slug);
      navigate(backTo);
    } catch (e) {
      setError(e.message || 'Não foi possível excluir o produto.');
    }
  };

  // Troca de foto reutiliza o MESMO fluxo da tela Fotos: upload → FOTO_EM_REVISAO →
  // comparação Original × Padronizada → aprovação humana. A foto aprovada anterior
  // continua no site (photoApproved) até a nova ser aprovada.
  const pickVariantPhoto = (variantId) => {
    photoTargetRef.current = variantId;
    fileRef.current?.click();
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoTargetRef.current) return;
    const vid = photoTargetRef.current;
    const v = variants.find((x) => x.id === vid);
    setError('');
    setPhotoBusy(vid);
    try {
      const dataUrl = await fileToDataUrl(file, 1200);
      const swapping = v?.photoStatus === PHOTO_STATUS.APROVADA;
      // Upload ao storage durável → FOTO_EM_REVISAO; a foto aprovada anterior (photoApproved)
      // continua pública até a nova ser aprovada.
      await patchVariantPhoto(
        product,
        vid,
        { photoOriginal: dataUrl, photoStatus: PHOTO_STATUS.REVISAO },
        user,
        swapping
          ? `Troca de foto solicitada para a variante “${v?.name}” — nova foto aguardando revisão; a foto atual continua no site.`
          : `Foto original enviada para a variante “${v?.name}” — aguardando revisão.`
      );
      setVariants((vs) => vs.map((x) => (x.id === vid ? { ...x, photoStatus: PHOTO_STATUS.REVISAO } : x)));
      setReviewPhoto({ variantId: vid, dataUrl });
    } catch (err) {
      setError(err.message || 'Falha ao enviar a foto.');
    } finally {
      setPhotoBusy(null);
      e.target.value = '';
    }
  };

  const approvePhoto = async (choice, std) => {
    const { variantId, dataUrl } = reviewPhoto;
    const finalImage = choice === 'original' ? dataUrl : std.standard;
    setError('');
    setPhotoBusy(variantId);
    try {
      await patchVariantPhoto(
        product,
        variantId,
        {
          image: finalImage,
          photoApproved: finalImage,
          photoThumb: std.thumb,
          photoOriginal: dataUrl,
          photoStatus: PHOTO_STATUS.APROVADA,
          // Origem da imagem aprovada: controla o selo "Foto ilustrativa" no site público.
          photoKind: choice === 'styled' ? 'estilizada' : choice === 'standard' ? 'padronizada' : 'original',
        },
        user,
        `Foto aprovada (${choice === 'standard' ? 'padronizada' : choice === 'styled' ? 'estilizada por IA' : 'original'}) para a variante “${variants.find((x) => x.id === variantId)?.name}” na edição do produto.`
      );
      setVariants((vs) => vs.map((x) => (x.id === variantId ? { ...x, image: finalImage, photoStatus: PHOTO_STATUS.APROVADA } : x)));
      setReviewPhoto(null);
    } catch (err) {
      setError(err.message || 'Falha ao salvar a foto.');
    } finally {
      setPhotoBusy(null);
    }
  };

  // Remover a foto principal (= foto da primeira variante): variante volta a FOTO_PENDENTE
  // e o site passa a exibir o placeholder "Foto em atualização" — nunca quebra o card.
  const removeMainPhoto = async () => {
    const v = variants[0];
    if (!v) return;
    try {
      await patchVariantPhoto(
        product,
        v.id,
        { image: null, photoApproved: null, photoThumb: null, photoOriginal: null, photoStatus: PHOTO_STATUS.PENDENTE },
        user,
        `Foto principal removida da variante “${v.name}” — aguardando nova foto.`
      );
      setVariants((vs) => vs.map((x, i) => (i === 0 ? { ...x, image: null, photoStatus: PHOTO_STATUS.PENDENTE } : x)));
      setConfirmRemovePhoto(false);
    } catch (err) {
      setError(err.message || 'Falha ao remover a foto.');
    }
  };

  if (!product) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="admin-edit-not-found">
        <p className="font-serif text-2xl text-navy">Produto não encontrado</p>
        <Link to="/admin/produtos" className="mt-4 inline-block text-sm font-semibold text-navy underline" data-testid="admin-edit-back-notfound">
          Voltar para Produtos
        </Link>
      </div>
    );
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVariant = (id, key, value) =>
    setVariants((vs) => vs.map((v) => (v.id === id ? { ...v, [key]: value } : v)));

  // id único de variante dentro do produto (mesmo padrão dos fluxos de cadastro).
  const aiVariantId = (name) => {
    const base = (name || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'variante';
    const ids = new Set(variants.map((v) => v.id));
    return ids.has(base) ? `${base}-${Date.now()}` : base;
  };

  // Variantes em lote com IA (componente compartilhado VariantAIAnalysis — o mesmo do
  // Cadastro em Lote): cria as variantes confirmadas NESTE produto, sem tocar nos dados
  // do produto-mãe nem nas variantes existentes. Sem foto individual → FOTO_PENDENTE.
  const confirmVariantAI = async (list) => {
    setAiBusy(true);
    setError('');
    setAiSuccess('');
    try {
      const created = [];
      for (const v of list) {
        const variant = {
          id: aiVariantId(v.name),
          name: v.name.trim(),
          code: (v.code || '').trim() || undefined,
          pattern: v.pattern,
          image: null,
          description: '',
          status: 'DISPONIVEL',
          statusHistory: [],
          photoStatus: PHOTO_STATUS.PENDENTE,
          createdAt: new Date().toISOString(),
          createdBy: user?.name,
        };
        await addVariantToFabric(slug, variant);
        created.push({ ...variant, isNew: false });
      }
      setVariants((vs) => [...vs, ...created]);
      setShowVariantAI(false);
      setAiSuccess(`${created.length} variante(s) criada(s) em “${product.name}”. Sem foto individual, ficam pendentes de foto — adicione quando puder.`);
    } catch (e) {
      setError('Não foi possível cadastrar as variantes. Tente novamente.');
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    setError('');
    setSaved(false);
    if (saving) return;
    if (!form.name.trim()) {
      setError('Informe o nome do tecido.');
      return;
    }
    if (!form.categorySlug) {
      setError('Escolha a categoria.');
      return;
    }
    if (!variantless) {
      if (activeVariants.some((v) => !v.name.trim())) {
        setError('Toda variante precisa de um nome.');
        return;
      }
      if (product.variants.length > 0 && activeVariants.length === 0) {
        setError('Este produto já possui variantes e precisa manter ao menos uma. Um produto novo sem variantes ainda deve nascer como “Produto mãe”.');
        return;
      }
    }
    setSaving(true);
    try {
      const patch = {
        name: form.name.trim(),
        categorySlug: form.categorySlug,
        subcategorySlug: form.subcategorySlug || undefined,
        description: form.description,
        width: form.width,
        composition: form.composition,
        gramatura: form.gramatura || undefined,
        elasticidade: form.elasticidade,
        unidadeVenda: form.unidadeVenda,
        fabricante: form.fabricante || undefined,
        observacoesTecnicas: form.observacoesTecnicas || undefined,
      };
      await updateCustomProduct(slug, patch);
      for (const id of removedIds) {
        await removeVariant(slug, id);
      }
      const assigned = new Set(variants.map((v) => v.id));
      for (const v of activeVariants) {
        if (v.isNew) {
          let vid = slugify(v.name.trim()) || `v${Date.now()}`;
          let n = 2;
          while (assigned.has(vid)) {
            vid = `${slugify(v.name.trim())}-${n}`;
            n += 1;
          }
          assigned.add(vid);
          await addVariantToFabric(slug, {
            id: vid,
            name: v.name.trim(),
            code: (v.code || '').trim() || undefined,
            pattern: v.pattern,
            description: v.description,
            image: v.image || null,
            status: 'DISPONIVEL',
            statusHistory: [],
            photoStatus: PHOTO_STATUS.PENDENTE,
            createdAt: new Date().toISOString(),
            createdBy: user?.name,
          });
        } else {
          await patchCustomVariant(slug, v.id, {
            name: v.name.trim(),
            code: (v.code || '').trim() || undefined,
            pattern: v.pattern,
            description: v.description,
          });
        }
      }
      setRemovedIds([]);
      const fresh = getProduct(slug);
      setVariants(
        (fresh?.variants || []).map((v) => ({
          id: v.id,
          name: v.name,
          code: v.code || '',
          pattern: v.pattern || 'Liso',
          description: v.description || '',
          image: v.image || null,
          photoStatus: getPhotoStatus(v),
        }))
      );
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Não foi possível salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-edit-page">
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline" data-testid="admin-edit-back">
        <ArrowLeft className="h-4 w-4" /> Voltar para Produtos
      </Link>
      <h2 className="mt-3 font-serif text-2xl text-navy">Editar produto</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        As alterações atualizam o cadastro existente — nenhum produto novo é criado. Fotos das variantes são
        gerenciadas na tela Fotos.
      </p>

      {error && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="admin-edit-error">{error}</p>
      )}
      {saved && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="admin-edit-saved">
          Alterações salvas no produto existente.
        </p>
      )}

      {!variantless && (
      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="admin-edit-main-photo">
        <h3 className="font-serif text-lg text-navy">Foto principal do produto</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A capa comercial (IA) aparece no card do catálogo e como foto inicial da página do produto — ao escolher uma
          variante, o cliente sempre vê a foto real. Sem capa, o site usa a foto da primeira variante.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          {cover ? (
            <img src={cover} alt={`Capa de ${form.name || product.name}`} className="h-24 w-24 rounded-xl object-cover" data-testid="edit-main-photo-preview" />
          ) : variants[0]?.image ? (
            <img src={variants[0].image} alt={variants[0].name} className="h-24 w-24 rounded-xl object-cover" data-testid="edit-main-photo-preview" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-xl bg-navy-light" data-testid="edit-main-photo-empty">
              <ImageOff className="h-6 w-6 text-navy/40" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            {variants.length > 0 && (
              <button
                type="button"
                onClick={() => pickVariantPhoto(variants[0].id)}
                data-testid="edit-main-photo-change"
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-navy px-5 text-xs font-bold text-white hover:bg-navy-hover"
              >
                <Camera className="h-4 w-4" /> TROCAR FOTO
              </button>
            )}
            {variants[0]?.image && !confirmRemovePhoto && (
              <button
                type="button"
                onClick={() => setConfirmRemovePhoto(true)}
                data-testid="edit-main-photo-remove"
                className="flex h-11 items-center justify-center gap-2 rounded-xl border border-red-300 px-5 text-xs font-bold text-red-700 hover:bg-red-50"
              >
                REMOVER FOTO
              </button>
            )}
            {confirmRemovePhoto && (
              <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-2" data-testid="edit-main-photo-remove-confirm">
                <button type="button" onClick={() => setConfirmRemovePhoto(false)} data-testid="edit-main-photo-remove-cancel" className="rounded-full border border-navy/20 px-3 py-2 text-[11px] font-semibold text-navy">
                  CANCELAR
                </button>
                <button type="button" onClick={removeMainPhoto} data-testid="edit-main-photo-remove-do" className="rounded-full bg-red-600 px-3 py-2 text-[11px] font-semibold text-white">
                  CONFIRMAR REMOÇÃO
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 border-t border-navy/10 pt-4">
          <CoverAIGenerator
            productName={form.name || product.name}
            variants={variants}
            cover={cover}
            onApprove={(dataUrl) => saveCover(dataUrl)}
            onRemove={() => saveCover(null)}
          />
        </div>
      </section>
      )}

      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="admin-edit-fabric">
        <h3 className="font-serif text-lg text-navy">Dados do tecido</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="edit-category" className={labelCls}>Categoria</label>
            <select id="edit-category" data-testid="edit-category" value={form.categorySlug} onChange={(e) => setForm((f) => ({ ...f, categorySlug: e.target.value, subcategorySlug: '' }))} className={inputCls}>
              {getAllCategories().map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-subcategory" className={labelCls}>Subcategoria (opcional)</label>
            <select id="edit-subcategory" data-testid="edit-subcategory" value={form.subcategorySlug} onChange={set('subcategorySlug')} className={inputCls}>
              <option value="">Sem subcategoria</option>
              {getSubcategories(form.categorySlug).map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-name" className={labelCls}>Nome do tecido *</label>
            <input id="edit-name" data-testid="edit-name" value={form.name} onChange={set('name')} className={inputCls} />
          </div>
          <div>
            <label htmlFor="edit-width" className={labelCls}>Largura</label>
            <input id="edit-width" data-testid="edit-width" value={form.width} onChange={set('width')} className={inputCls} placeholder="Ex.: 1,50 m" />
          </div>
          <div>
            <label htmlFor="edit-composition" className={labelCls}>Composição</label>
            <input id="edit-composition" data-testid="edit-composition" value={form.composition} onChange={set('composition')} className={inputCls} placeholder="Ex.: 100% poliéster" />
          </div>
          <div>
            <label htmlFor="edit-gramatura" className={labelCls}>Gramatura (opcional)</label>
            <input id="edit-gramatura" data-testid="edit-gramatura" value={form.gramatura} onChange={set('gramatura')} className={inputCls} placeholder="Ex.: 180 g/m²" />
          </div>
          <div>
            <label htmlFor="edit-elasticidade" className={labelCls}>Elasticidade</label>
            <select id="edit-elasticidade" data-testid="edit-elasticidade" value={form.elasticidade} onChange={set('elasticidade')} className={inputCls}>
              {ELASTICIDADE_OPTS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="edit-unit" className={labelCls}>Unidade de venda</label>
            <select id="edit-unit" data-testid="edit-unit" value={form.unidadeVenda} onChange={set('unidadeVenda')} className={inputCls}>
              {UNIDADE_OPTS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="edit-manufacturer" className={labelCls}>Fabricante / Linha (opcional)</label>
            <input id="edit-manufacturer" data-testid="edit-manufacturer" value={form.fabricante} onChange={set('fabricante')} className={inputCls} />
          </div>
        </div>
        <div className="mt-4">
          <label htmlFor="edit-description" className={labelCls}>Descrição</label>
          <textarea id="edit-description" data-testid="edit-description" value={form.description} onChange={set('description')} rows={3} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
        </div>
        <div className="mt-4">
          <label htmlFor="edit-tech-notes" className={labelCls}>Observações técnicas (opcional)</label>
          <textarea id="edit-tech-notes" data-testid="edit-tech-notes" value={form.observacoesTecnicas} onChange={set('observacoesTecnicas')} rows={2} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
        </div>
      </section>

      {variantless ? (
        <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="admin-edit-variantless-note">
          <h3 className="font-serif text-lg text-navy">Produto sem variantes</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            O próprio produto é a unidade comercial: o cliente informa a metragem diretamente, sem escolher
            cor/estampa. Nenhuma variante fictícia (“Padrão”, “Opção”...) é criada ou exibida no site.
          </p>
        </section>
      ) : (
      <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid="admin-edit-variants">
        <h3 className="font-serif text-lg text-navy">Variantes ({activeVariants.length})</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Cor / Estampa / Variante, código interno, padrão e descrição. Você pode adicionar várias variantes e
          remover existentes — as remoções só acontecem ao salvar.
        </p>
        <div className="mt-4 space-y-4">
          {variants.map((v) => {
            if (removedIds.includes(v.id)) {
              return (
                <div key={v.id} className="rounded-xl border border-red-300 bg-red-50/50 p-4" data-testid={`edit-variant-removed-${v.id}`}>
                  <p className="text-sm font-medium text-red-800">“{v.name}” será removida ao salvar.</p>
                  <button
                    type="button"
                    onClick={() => setRemovedIds((ids) => ids.filter((x) => x !== v.id))}
                    data-testid={`edit-variant-undo-remove-${v.id}`}
                    className="mt-2 rounded-full border border-navy/25 px-4 py-2 text-xs font-semibold text-navy hover:bg-navy-light"
                  >
                    DESFAZER REMOÇÃO
                  </button>
                </div>
              );
            }
            return (
            <div key={v.id} className="rounded-xl border border-navy/10 p-4" data-testid={`edit-variant-${v.id}`}>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                {v.isNew ? (
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`edit-variant-new-badge-${v.id}`}>
                    Nova variante — será criada ao salvar
                  </span>
                ) : (
                  <>
                    {v.image ? (
                      <img src={v.image} alt={v.name} className="h-14 w-14 shrink-0 rounded-lg object-cover" data-testid={`edit-variant-photo-preview-${v.id}`} />
                    ) : (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-navy-light" data-testid={`edit-variant-photo-empty-${v.id}`}>
                        <ImageOff className="h-5 w-5 text-navy/40" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => pickVariantPhoto(v.id)}
                      data-testid={`edit-variant-photo-${v.id}`}
                      className="flex h-10 items-center gap-2 rounded-xl border border-navy/20 px-4 text-xs font-semibold text-navy hover:bg-navy-light"
                    >
                      <Camera className="h-4 w-4" /> TROCAR FOTO
                    </button>
                    {v.photoStatus && v.photoStatus !== PHOTO_STATUS.APROVADA && (
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`edit-variant-photo-status-${v.id}`}>
                        {v.photoStatus === PHOTO_STATUS.REVISAO ? 'Foto em revisão' : 'Foto pendente'}
                      </span>
                    )}
                  </>
                )}
                <button
                  type="button"
                  onClick={() => (v.isNew ? setVariants((vs) => vs.filter((x) => x.id !== v.id)) : setRemovedIds((ids) => [...ids, v.id]))}
                  disabled={!v.isNew && activeVariants.length <= 1}
                  data-testid={`edit-variant-remove-${v.id}`}
                  aria-label={`Remover variante ${v.name || 'nova'}`}
                  className="ml-auto flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" /> REMOVER
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label htmlFor={`edit-variant-name-${v.id}`} className={labelCls}>Cor / Estampa / Variante *</label>
                  <input id={`edit-variant-name-${v.id}`} data-testid={`edit-variant-name-${v.id}`} value={v.name} onChange={(e) => setVariant(v.id, 'name', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`edit-variant-code-${v.id}`} className={labelCls}>Código interno (opcional)</label>
                  <input id={`edit-variant-code-${v.id}`} data-testid={`edit-variant-code-${v.id}`} value={v.code} onChange={(e) => setVariant(v.id, 'code', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`edit-variant-pattern-${v.id}`} className={labelCls}>Padrão</label>
                  <select id={`edit-variant-pattern-${v.id}`} data-testid={`edit-variant-pattern-${v.id}`} value={v.pattern} onChange={(e) => setVariant(v.id, 'pattern', e.target.value)} className={inputCls}>
                    {PATTERN_OPTS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label htmlFor={`edit-variant-desc-${v.id}`} className={labelCls}>Descrição da variante (opcional)</label>
                <input id={`edit-variant-desc-${v.id}`} data-testid={`edit-variant-desc-${v.id}`} value={v.description} onChange={(e) => setVariant(v.id, 'description', e.target.value)} className={inputCls} />
              </div>
            </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addVariantRow}
          data-testid="edit-variant-add"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-navy/25 px-4 py-4 text-sm font-semibold text-navy hover:bg-navy-light"
        >
          <Plus className="h-4 w-4" /> ADICIONAR VARIANTE
        </button>
        <button
          type="button"
          onClick={() => { setShowVariantAI((s) => !s); setAiSuccess(''); }}
          data-testid="edit-variant-ai-batch"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-4 text-sm font-bold text-white hover:bg-navy-hover"
        >
          ADICIONAR VARIANTES EM LOTE COM IA
        </button>
        {aiSuccess && (
          <p className="mt-3 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-800" data-testid="edit-variant-ai-success">
            {aiSuccess}
          </p>
        )}
        {showVariantAI && (
          <div className="mt-4" data-testid="edit-variant-ai-panel">
            <VariantAIAnalysis
              testidPrefix="edit"
              productName={product.name}
              existingVariants={activeVariants}
              confirmLabel={aiBusy ? 'CADASTRANDO…' : 'CADASTRAR VARIANTES SELECIONADAS'}
              confirmTestid="edit-variant-ai-confirm"
              confirmDisabled={aiBusy}
              onConfirm={confirmVariantAI}
              onBack={() => setShowVariantAI(false)}
              backLabel="FECHAR"
              fotoTitle="Fotos do conjunto — análise com IA"
              reviewTitle="Sugestões da IA — revisão antes de cadastrar"
            />
          </div>
        )}
      </section>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light"
          data-testid="admin-edit-cancel"
        >
          CANCELAR
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
          data-testid="admin-edit-save"
        >
          <Check className="h-4 w-4" /> {saving ? 'SALVANDO…' : 'SALVAR ALTERAÇÕES'}
        </button>
      </div>

      <section className="mt-8 rounded-2xl border-2 border-dashed border-red-300 bg-red-50/40 p-5" data-testid="admin-edit-danger-zone">
        <h3 className="font-serif text-lg text-red-800">Excluir produto</h3>
        <p className="mt-1 text-xs text-red-700/80">
          Remove “{form.name || product.name}”{variantless ? '' : activeVariants.length === 1 ? ' e sua 1 variante' : ` e suas ${activeVariants.length} variantes`} do catálogo. Solicitações já enviadas não são alteradas.
        </p>
        {confirmDelete ? (
          <div className="mt-4 rounded-xl border border-red-300 bg-white p-4" data-testid="admin-edit-delete-confirm">
            <p className="text-sm font-medium text-red-800">
              Confirmar exclusão de “{form.name || product.name}”? Esta ação não pode ser desfeita.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                data-testid="admin-edit-delete-cancel"
                className="rounded-full border border-navy/20 px-5 py-2.5 text-xs font-semibold text-navy hover:bg-navy-light"
              >
                CANCELAR
              </button>
              <button
                type="button"
                onClick={doDelete}
                data-testid="admin-edit-delete-do"
                className="rounded-full bg-red-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-red-700"
              >
                EXCLUIR DEFINITIVAMENTE
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            data-testid="admin-edit-delete"
            className="mt-4 flex items-center gap-2 rounded-full border border-red-400 px-5 py-3 text-xs font-bold text-red-700 transition-colors hover:bg-red-100"
          >
            EXCLUIR PRODUTO
          </button>
        )}
      </section>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={onPhotoFile} data-testid="edit-photo-file-input" />{reviewPhoto && (
        <PhotoReviewModal
          original={reviewPhoto.dataUrl}
          variantName={variants.find((v) => v.id === reviewPhoto.variantId)?.name || ''}
          productName={product?.name || ''}
          onChoose={approvePhoto}
          onRetake={() => {
            const vid = reviewPhoto.variantId;
            setReviewPhoto(null);
            pickVariantPhoto(vid);
          }}
          onClose={() => setReviewPhoto(null)}
        />
      )}
    </div>
  );
}
